import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { RefreshCw, Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Euro, Zap, List, BarChart3, MessageSquareX, X, Trash2, FlaskConical, HeartPulse } from 'lucide-react';
import { SubscriberStats } from './SubscriberStats';
import { SubscriberDrawer } from './SubscriberDrawer';
import { RevenueTab } from './RevenueTab';
import { TestAccountsPanel } from './TestAccountsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLS = [
  { id: 'email', label: 'Utilisateur' },
  { id: 'plan_label', label: 'Offre' },
  { id: 'status', label: 'Statut' },
  { id: 'amount', label: 'Prix' },
  { id: 'total_paid', label: 'Total payé' },
  { id: 'account_created_at', label: 'Compte créé le' },
  { id: 'started_at', label: 'Abonné depuis' },
  { id: 'canceled_at', label: 'Résiliation demandée' },
  { id: 'current_period_end', label: 'Accès jusqu\'au' },
  { id: 'subs_count', label: 'Abos' },
  { id: 'last_payment_at', label: 'Dernier paiement' },
];

const FILTERS = [
  { id: 'all', label: 'Tous', key: 'total' },
  { id: 'new', label: 'Nouveaux (mois)', key: 'new' },
  { id: 'active', label: 'Actifs', key: 'active' },
  { id: 'cancel_pending', label: 'Résiliation en cours', key: 'cancel_pending' },
  { id: 'past_due', label: 'Impayés', key: 'past_due' },
  { id: 'canceled_payment', label: 'Résiliés — impayé', key: 'canceled_payment' },
  { id: 'canceled', label: 'Résiliés — volontaire', key: 'canceled' },
  { id: 'resub', label: 'Réabonnés', key: 'resub' },
  { id: 'never', label: 'Jamais abonnés', key: 'never' },
];

const money = (cents, cur = 'eur') => `${((cents || 0) / 100).toFixed(2)} ${cur === 'eur' ? '€' : cur.toUpperCase()}`;
const fdate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
const fdatetime = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

function StatusBadge({ r }) {
  const cape = r.cancel_at_period_end;
  const st = r.sub_status;
  const payFail = r.cancellation_reason === 'payment_failed';
  let cls = 'bg-white/10 text-white/50', label = 'Jamais abonné';
  if (cape && ['active', 'trialing', 'past_due'].includes(st)) { cls = 'bg-amber-500/15 text-amber-400'; label = 'Résiliation en cours'; }
  else if (st === 'active') { cls = 'bg-green-500/15 text-green-400'; label = 'Actif'; }
  else if (st === 'trialing') { cls = 'bg-blue-500/15 text-blue-400'; label = 'Essai'; }
  else if (st === 'past_due') { cls = 'bg-red-500/15 text-red-400'; label = 'Impayé — relance'; }
  else if (st === 'unpaid' || (st === 'canceled' && payFail)) { cls = 'bg-red-500/25 text-red-300'; label = 'Résilié — impayé'; }
  else if (st === 'canceled') { cls = 'bg-red-500/10 text-red-400/80'; label = 'Résilié'; }
  else if (st === 'incomplete' || st === 'incomplete_expired') { cls = 'bg-white/10 text-white/40'; label = 'Incomplet'; }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${cls}`}>{label}</span>
      {r.resubscribed && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-500/15 text-purple-400 whitespace-nowrap" title={`${r.subs_count} abonnements au total`}>Réabonné</span>}
      {r.access_blocked && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-500/20 text-red-400 whitespace-nowrap" title="Accès Maya+ bloqué manuellement par l'admin" data-testid={`tag-bloque-${r.email}`}>Bloqué</span>}
      {r.access_override && !r.access_blocked && !['active', 'trialing'].includes(r.sub_status) && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-500/15 text-teal-400 whitespace-nowrap" title="Accès offert manuellement sans abonnement" data-testid={`tag-gratuit-${r.email}`}>Gratuit</span>}
    </span>
  );
}

export function SubscribersTab({ theme }) {
  const light = theme === 'light';
  const [view, setView] = useState('list'); // list | stats
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('recent_at');
  const [order, setOrder] = useState('desc');
  const [filter, setFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [drawerRow, setDrawerRow] = useState(null);
  const [showReasons, setShowReasons] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const searchTimer = useRef(null);
  const perPage = 25;

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/subscribers`, {
        params: { page, per_page: perPage, sort_by: sortBy, order, filter_status: filter, filter_plan: planFilter, search },
        withCredentials: true,
      });
      setData(d);
      setSyncing(!!d.syncing);
    } catch { /* ignore */ }
  }, [page, sortBy, order, filter, planFilter, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!syncing) return;
    const t = setInterval(() => load(), 5000);
    return () => clearInterval(t);
  }, [syncing, load]);

  const startSync = async () => {
    setSyncing(true);
    try { await axios.post(`${API}/admin/subscribers/sync`, {}, { withCredentials: true }); } catch { setSyncing(false); }
  };

  const [whLoading, setWhLoading] = useState(false);
  const setupWebhook = async () => {
    setWhLoading(true);
    try {
      await axios.post(`${API}/admin/subscribers/webhook-setup`, {}, { withCredentials: true });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur webhook'); }
    setWhLoading(false);
  };

  const deleteUser = async (r) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${r.email} ?\n\nCette personne sera retirée de la liste et devra créer un nouveau compte pour se reconnecter. L'ancien compte ne pourra plus être utilisé.`)) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${encodeURIComponent(r.email)}`, { withCredentials: true });
      load();
    } catch (err) { alert(err.response?.data?.detail || `Erreur lors de la suppression (code ${err.response?.status || 'réseau'})`); }
  };

  const onSearch = (v) => {
    setSearchInput(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); setSearch(v); }, 400);
  };

  const clickSort = (col) => {
    if (sortBy === col) setOrder(o => (o === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setOrder('desc'); }
    setPage(1);
  };

  const s = data?.summary || {};
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / perPage));
  const th = light ? 'text-gray-500' : 'text-white/40';
  const cardCls = light ? 'bg-white border-gray-200' : 'bg-white/[0.03] border-white/10';

  return (
    <div data-testid="subscribers-tab">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className={`text-2xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Abonnés ({data?.summary?.total ?? '…'})</h1>
          <p className={`text-xs mt-1 ${th}`}>
            {data?.synced_at ? `Données Stripe synchronisées le ${new Date(data.synced_at).toLocaleString('fr-FR')}` : 'Aucune synchronisation Stripe — cliquez sur Synchroniser'}
            {data?.sync_error && <span className="text-red-400"> · Erreur : {data.sync_error}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`flex items-center gap-1 text-xs font-semibold ${light ? 'text-gray-700' : 'text-white/70'}`} data-testid="subscribers-revenue">
            <Euro size={13} className="text-[#E9641A]" /> Revenu total : {money(s.revenue)}
          </span>
          {data?.webhook_configured ? (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 text-green-400 text-[11px] font-semibold rounded-lg" title={data?.last_webhook_at ? `Dernier événement Stripe : ${new Date(data.last_webhook_at).toLocaleString('fr-FR')}` : 'En attente du premier événement Stripe'} data-testid="webhook-active-badge">
              <Zap size={12} /> Synchro instantanée active
            </span>
          ) : (
            <button onClick={setupWebhook} disabled={whLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white/70 text-[11px] font-semibold rounded-lg hover:bg-white/20 transition-colors disabled:opacity-60"
              data-testid="webhook-setup-btn">
              <Zap size={12} /> {whLoading ? 'Activation…' : 'Activer la synchro instantanée'}
            </button>
          )}
          <button onClick={startSync} disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 bg-[#E9641A] text-white text-xs font-semibold rounded-lg hover:bg-[#FE975E] transition-colors disabled:opacity-60"
            data-testid="subscribers-sync-btn">
            <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Synchronisation…' : 'Synchroniser Stripe'}
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className={`flex items-center gap-1 mb-5 border-b ${light ? 'border-gray-200' : 'border-white/10'}`}>
        {[{ id: 'list', label: 'Liste', icon: List }, { id: 'stats', label: 'Statistiques', icon: BarChart3 }, { id: 'revenue', label: 'Revenus', icon: Euro }, { id: 'test', label: 'Comptes Test', icon: FlaskConical }].map(t => (
          <button key={t.id} onClick={() => setView(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${view === t.id ? 'border-[#E9641A] text-[#E9641A]' : `border-transparent ${light ? 'text-gray-500 hover:text-gray-800' : 'text-white/40 hover:text-white/80'}`}`}
            data-testid={`subscribers-view-${t.id}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {view === 'stats' ? <SubscriberStats theme={theme} /> : view === 'revenue' ? <RevenueTab theme={theme} /> : view === 'test' ? <TestAccountsPanel theme={theme} /> : (<>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => { setFilter(f.id); setPage(1); if (f.id === 'new') { setSortBy('started_at'); setOrder('desc'); } }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${filter === f.id ? 'bg-[#E9641A] text-white' : light ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
            data-testid={`subscribers-filter-${f.id}`}>
            {f.label}{s[f.key] !== undefined ? ` (${s[f.key]})` : ''}
          </button>
        ))}
        <button onClick={() => setShowReasons(true)} data-testid="subscribers-reasons-btn"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${light ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}>
          <MessageSquareX size={12} /> Raisons de résiliation
        </button>
        <button onClick={() => setShowRecovery(true)} data-testid="subscribers-recovery-btn"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${light ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}>
          <HeartPulse size={12} /> Relances impayés
        </button>
        <select value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
          className={`text-xs rounded-full px-3 py-1.5 border font-semibold focus:outline-none ${planFilter ? 'border-[#E9641A] text-[#E9641A]' : ''} ${light ? 'bg-white border-gray-200 text-gray-600' : 'bg-white/5 border-white/10 text-white/60'}`}
          data-testid="subscribers-plan-filter">
          <option value="">Toutes les offres</option>
          {(s.plans || []).map(p => <option key={p.label} value={p.label}>{p.label} ({p.active} actifs / {p.n})</option>)}
        </select>
        <div className="relative ml-auto">
          <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${th}`} />
          <input value={searchInput} onChange={e => onSearch(e.target.value)} placeholder="Rechercher email ou nom…"
            className={`pl-9 pr-3 py-2 rounded-lg text-xs w-64 focus:outline-none focus:border-[#E9641A] border ${light ? 'bg-white border-gray-200 text-gray-800' : 'bg-white/5 border-white/10 text-white'}`}
            data-testid="subscribers-search" />
        </div>
      </div>

      {/* Table */}
      <div className={`border rounded-xl overflow-x-auto ${cardCls}`}>
        <table className="w-full text-left" data-testid="subscribers-table">
          <thead>
            <tr className={light ? 'border-b border-gray-200' : 'border-b border-white/10'}>
              {COLS.map(c => (
                <th key={c.id} onClick={() => clickSort(c.id)}
                  className={`px-3 py-3 text-[10px] font-semibold uppercase tracking-wide cursor-pointer select-none whitespace-nowrap hover:text-[#E9641A] transition-colors ${sortBy === c.id ? 'text-[#E9641A]' : th}`}
                  data-testid={`sort-${c.id}`}>
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {sortBy === c.id && (order === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                  </span>
                </th>
              ))}
              <th className={`px-3 py-3 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap ${th}`} />
            </tr>
          </thead>
          <tbody className={light ? 'divide-y divide-gray-100' : 'divide-y divide-white/5'}>
            {(data?.rows || []).map((r, i) => (
              <tr key={r.email + i} onClick={() => setDrawerRow(r)}
                className={`cursor-pointer ${light ? 'hover:bg-gray-50' : 'hover:bg-white/[0.02]'}`} data-testid={`subscriber-row-${i}`}>
                <td className="px-3 py-3">
                  <p className={`text-xs font-semibold ${light ? 'text-gray-900' : 'text-white'}`}>{r.name || '—'}</p>
                  <p className={`text-[10px] ${th}`}>{r.email || r.customer_id || '—'}</p>
                  {r.phone && <p className={`text-[10px] ${th}`}>{r.phone}</p>}
                  {r.has_account === false && <span className="text-[9px] text-amber-400/80">Sans compte Maya+</span>}
                </td>
                <td className={`px-3 py-3 text-xs whitespace-nowrap ${light ? 'text-gray-700' : 'text-white/80'}`}>{r.plan_label || '—'}</td>
                <td className="px-3 py-3"><StatusBadge r={r} /></td>
                <td className={`px-3 py-3 text-xs whitespace-nowrap tabular-nums ${light ? 'text-gray-700' : 'text-white/80'}`}>
                  {r.amount ? `${money(r.amount)} / ${r.interval === 'year' ? 'an' : 'mois'}` : '—'}
                </td>
                <td className={`px-3 py-3 text-xs font-bold whitespace-nowrap tabular-nums ${light ? 'text-gray-900' : 'text-white'}`}>
                  {money(r.total_paid)}{r.payments_count ? <span className={`font-normal ${th}`}> ({r.payments_count})</span> : ''}
                </td>
                <td className={`px-3 py-3 text-xs whitespace-nowrap ${light ? 'text-gray-600' : 'text-white/60'}`} data-testid={`account-created-${i}`}>
                  {r.account_created_at ? fdatetime(r.account_created_at) : <span className={th} title="Client Stripe sans compte Maya+">—</span>}
                </td>
                <td className={`px-3 py-3 text-xs whitespace-nowrap ${light ? 'text-gray-600' : 'text-white/60'}`}>{fdate(r.started_at)}</td>
                <td className="px-3 py-3 text-xs whitespace-nowrap">
                  {r.cancel_at_period_end && r.canceled_at ? <span className="text-amber-400">{fdatetime(r.canceled_at)}</span>
                    : r.sub_status === 'canceled' && r.canceled_at ? <span className="text-red-400/80">{fdatetime(r.canceled_at)}</span>
                    : <span className={th}>—</span>}
                </td>
                <td className="px-3 py-3 text-xs whitespace-nowrap">
                  {r.cancel_at_period_end && r.current_period_end ? <span className="text-amber-400 font-semibold">{fdate(r.current_period_end)}</span>
                    : ['active', 'trialing'].includes(r.sub_status) ? <span className={light ? 'text-gray-600' : 'text-white/60'}>{fdate(r.current_period_end)}</span>
                    : <span className={th}>—</span>}
                </td>
                <td className={`px-3 py-3 text-xs tabular-nums ${r.subs_count > 1 ? 'text-purple-400 font-bold' : light ? 'text-gray-600' : 'text-white/60'}`}>{r.subs_count || 0}</td>
                <td className={`px-3 py-3 text-xs whitespace-nowrap ${light ? 'text-gray-600' : 'text-white/60'}`}>{fdate(r.last_payment_at)}</td>
                <td className="px-2 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                  {!['active', 'trialing', 'past_due'].includes(r.sub_status) && r.email && (
                    <button onClick={() => deleteUser(r)} title="Supprimer ce compte" data-testid={`delete-user-${i}`}
                      className={`p-1.5 rounded transition-colors ${light ? 'text-gray-300 hover:text-red-500 hover:bg-red-50' : 'text-white/20 hover:text-red-400 hover:bg-red-500/10'}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {data && data.rows?.length === 0 && (
              <tr><td colSpan={COLS.length + 1} className={`px-3 py-10 text-center text-xs ${th}`}>Aucun résultat</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <p className={`text-[11px] ${th}`}>{data?.total ?? 0} utilisateur(s) · page {page}/{totalPages}</p>
        <div className="flex items-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
            className={`p-2 rounded-lg disabled:opacity-30 ${light ? 'bg-gray-100 text-gray-600' : 'bg-white/5 text-white/60'}`} data-testid="subscribers-prev"><ChevronLeft size={14} /></button>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className={`p-2 rounded-lg disabled:opacity-30 ${light ? 'bg-gray-100 text-gray-600' : 'bg-white/5 text-white/60'}`} data-testid="subscribers-next"><ChevronRight size={14} /></button>
        </div>
      </div>
      </>)}

      {drawerRow && <SubscriberDrawer row={drawerRow} onClose={() => setDrawerRow(null)} theme={theme} onCanceled={() => load()} />}
      {showReasons && <CancelReasonsModal light={light} onClose={() => setShowReasons(false)} />}
      {showRecovery && <PastDueRecoveryModal light={light} onClose={() => setShowRecovery(false)} />}
    </div>
  );
}

function PastDueRecoveryModal({ light, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/relance/past-due-stats`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => setData({}));
  }, []);
  const hintCls = light ? 'text-gray-400' : 'text-white/35';
  const cardCls = light ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.03] border-white/5';
  const d = data || {};
  const totalRelanced = (d.recovered_after_relance || 0) + (d.lost_after_relance || 0);
  const rate = totalRelanced ? Math.round((d.recovered_after_relance / totalRelanced) * 100) : null;
  const stats = [
    { label: 'Impayés en cours', value: d.currently_past_due, color: 'text-red-400' },
    { label: 'Relances J+2 envoyées', value: d.relance1_sent, color: 'text-[#E9641A]' },
    { label: 'Derniers rappels J+5', value: d.relance2_sent, color: 'text-[#E9641A]' },
    { label: 'Récupérés après relance', value: d.recovered_after_relance, color: 'text-emerald-400' },
    { label: 'Récupérés seuls (avant relance)', value: d.recovered_alone, color: 'text-emerald-400/70' },
    { label: 'Perdus malgré la relance', value: d.lost_after_relance, color: 'text-red-400/80' },
  ];
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="past-due-recovery-modal">
      <div className={`rounded-xl border w-full max-w-2xl max-h-[88vh] overflow-y-auto p-6 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className={`text-lg font-bold flex items-center gap-2 ${light ? 'text-gray-900' : 'text-white'}`}>
            <HeartPulse size={18} className="text-[#E9641A]" /> Relances impayés — abonnements sauvés
          </h3>
          <button onClick={onClose} className={light ? 'text-gray-400 hover:text-gray-700' : 'text-white/40 hover:text-white'} data-testid="recovery-modal-close"><X size={18} /></button>
        </div>
        <p className={`text-xs mb-5 ${hintCls}`}>Relance automatique à J+2 puis dernier rappel à J+5 après un paiement rejeté. Un abonné est « récupéré » quand il redevient actif après être passé en impayé.</p>
        {!data ? <p className={`text-center text-sm py-8 ${hintCls}`}>Chargement…</p> : (
          <>
            {rate !== null && (
              <div className={`rounded-lg border p-4 mb-4 text-center ${cardCls}`} data-testid="recovery-rate">
                <p className="text-3xl font-extrabold text-emerald-400">{rate}%</p>
                <p className={`text-xs mt-1 ${hintCls}`}>taux de récupération après relance ({d.recovered_after_relance}/{totalRelanced})</p>
                {(d.saved_monthly_cents || 0) > 0 && (
                  <p className="text-xs mt-1 font-semibold text-[#E9641A]">≈ {((d.saved_monthly_cents || 0) / 100).toFixed(2).replace('.', ',')} € de revenus récurrents sauvés</p>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
              {stats.map(s => (
                <div key={s.label} className={`rounded-lg border p-3 ${cardCls}`}>
                  <p className={`text-xl font-extrabold ${s.color}`}>{s.value ?? 0}</p>
                  <p className={`text-[10px] mt-0.5 ${hintCls}`}>{s.label}</p>
                </div>
              ))}
            </div>
            {(d.recent || []).length > 0 ? (
              <>
                <p className={`text-[10px] uppercase tracking-wider mb-2 ${hintCls}`}>Dernières issues</p>
                <div className="space-y-1.5">
                  {d.recent.map(r => (
                    <div key={r.id} className={`rounded-lg border px-3 py-2 flex items-center gap-2 flex-wrap ${cardCls}`}>
                      <span className={`text-xs font-medium ${light ? 'text-gray-700' : 'text-white/80'}`}>{r.name || r.email}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${r.outcome === 'recovered' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                        {r.outcome === 'recovered' ? 'Récupéré' : 'Perdu'}{r.stage_reached > 0 ? ` · après relance ${r.stage_reached === 2 ? 'J+5' : 'J+2'}` : ' · sans relance'}
                      </span>
                      <span className={`text-[10px] ml-auto ${hintCls}`}>{new Date(r.resolved_at).toLocaleString('fr-FR')}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className={`text-center text-sm py-6 ${hintCls}`}>Aucune issue enregistrée pour le moment — les impayés en cours sont suivis automatiquement.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CancelReasonsModal({ light, onClose }) {
  const [data, setData] = useState(null);
  const [retention, setRetention] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/cancellation-reasons`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => setData({ total: 0, breakdown: [], recent: [] }));
    axios.get(`${API}/admin/retention/stats`, { withCredentials: true })
      .then(r => setRetention(r.data)).catch(() => {});
  }, []);
  const hintCls = light ? 'text-gray-400' : 'text-white/35';
  const cardCls = light ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.03] border-white/5';
  const max = data && data.breakdown.length ? data.breakdown[0].count : 1;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="cancel-reasons-modal">
      <div className={`rounded-xl border w-full max-w-2xl max-h-[88vh] overflow-y-auto p-6 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className={`text-lg font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Raisons de résiliation</h3>
          <button onClick={onClose} className={light ? 'text-gray-400 hover:text-gray-700' : 'text-white/40 hover:text-white'} data-testid="cancel-reasons-close"><X size={18} /></button>
        </div>
        <p className={`text-xs mb-5 ${hintCls}`}>Réponses au mini-sondage affiché lors d'une résiliation ({data ? data.total : '…'} au total).</p>
        {retention && retention.shown > 0 && (
          <div className={`rounded-lg border p-3 mb-5 ${cardCls}`} data-testid="retention-stats">
            <p className="text-xs font-semibold text-[#E9641A] mb-1">Offres de rétention « Trop cher »</p>
            <p className={`text-xs ${hintCls}`}>
              {retention.shown} proposée{retention.shown > 1 ? 's' : ''} ·{' '}
              <span className="text-emerald-500 font-bold">{retention.accepted} acceptée{retention.accepted > 1 ? 's' : ''}</span> ·{' '}
              {retention.declined} refusée{retention.declined > 1 ? 's' : ''} ·
              taux de sauvetage {Math.round(retention.accepted / Math.max(retention.shown, 1) * 100)}%
              {retention.winback_sent > 0 && <> · {retention.winback_sent} relance{retention.winback_sent > 1 ? 's' : ''} email envoyée{retention.winback_sent > 1 ? 's' : ''}</>}
            </p>
          </div>
        )}
        {data && (
          <>
            <div className="space-y-2 mb-6">
              {data.breakdown.map(b => (
                <div key={b.reason} className={`rounded-lg border p-3 ${cardCls}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`text-xs font-semibold ${light ? 'text-gray-700' : 'text-white/80'}`}>{b.reason}</span>
                    <span className="text-xs font-bold text-[#E9641A]">{b.count} · {Math.round(b.count / Math.max(data.total, 1) * 100)}%</span>
                  </div>
                  <div className={`h-1.5 rounded-full overflow-hidden ${light ? 'bg-gray-200' : 'bg-white/5'}`}>
                    <div className="h-full rounded-full bg-[#E9641A]" style={{ width: `${(b.count / max) * 100}%` }} />
                  </div>
                </div>
              ))}
              {data.breakdown.length === 0 && <p className={`text-center text-sm py-6 ${hintCls}`}>Aucune résiliation enregistrée pour le moment.</p>}
            </div>
            {data.recent.length > 0 && (
              <>
                <p className={`text-[10px] uppercase tracking-wider mb-2 ${hintCls}`}>Dernières résiliations</p>
                <div className="space-y-1.5">
                  {data.recent.map(r => (
                    <div key={r.id} className={`rounded-lg border px-3 py-2 ${cardCls}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-medium ${light ? 'text-gray-700' : 'text-white/80'}`}>{r.email}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${r.source === 'admin' ? 'bg-blue-500/15 text-blue-400' : 'bg-[#E9641A]/15 text-[#E9641A]'}`}>{r.reason}</span>
                        <span className={`text-[10px] ml-auto ${hintCls}`}>{new Date(r.created_at).toLocaleString('fr-FR')}</span>
                      </div>
                      {r.comment && <p className={`text-[11px] mt-1 italic ${hintCls}`}>« {r.comment} »</p>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
